import React, { useEffect, useState, useCallback, useMemo, useId, useRef } from "react";
import { fetchTheme, DEFAULT_THEME } from "./settingsApi";
import { fetchLedProducts, computeLedLayout } from "./ledConfiguratorApi";
import ModeSwitcher from "./ModeSwitcher";
import LogoMLV from "./assets/logo-mlv.png";
import PersonSilhouette from "./assets/person-silhouette.png";
import ScreenContentDemo from "./assets/content-aurora.jpg";

// Rasio asli file gambar orang (lebar / tinggi) -- dipakai supaya siluet
// tidak gepeng saat digambar di canvas dengan tinggi berapa pun.
const PERSON_IMG_RATIO = 120 / 432;

const inputStyle = {
  width: "100%", padding: "9px 11px", borderRadius: 8, border: "1px solid var(--app-border)",
  fontSize: 13, outline: "none", boxSizing: "border-box", background: "var(--app-bg)", color: "var(--app-text)",
};
const labelStyle = { fontSize: 11.5, fontWeight: 700, color: "var(--app-text-muted)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.3 };

function fmt(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString("id-ID", { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

// -- Konversi & format satuan panjang (m <-> ft), dipakai khusus untuk
// label-label yang ditampilkan di atas canvas (input di panel kiri
// tetap selalu dalam meter, supaya perhitungan cabinet tidak berubah).
const M_TO_FT = 3.28084;
function formatLen(m, unit, digits = 2) {
  if (m === null || m === undefined || Number.isNaN(m)) return "—";
  if (unit === "ft") return `${fmt(m * M_TO_FT, digits)} ft`;
  return `${fmt(m, digits)} m`;
}

export default function LedConfigurator() {
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [environment, setEnvironment] = useState("indoor");
  const [productId, setProductId] = useState("");

  // Ukuran layar LED yang diinginkan (dipakai untuk hitung susunan cabinet).
  // Nilai awal 1x1 -- akan langsung disesuaikan ke ukuran 1 cabinet penuh
  // begitu produk terpilih (lihat effect di bawah).
  const [wallWidthM, setWallWidthM] = useState(1);
  const [wallHeightM, setWallHeightM] = useState(1);

  // Ukuran ruangan / dinding tempat layar dipasang -- ini yang membuat
  // canvas menampilkan dinding secara utuh (gaya MAXHUB LED Configurator),
  // bukan cuma layarnya saja.
  const [roomWidthM, setRoomWidthM] = useState(5);
  const [roomHeightM, setRoomHeightM] = useState(3);

  const [unit, setUnit] = useState("m"); // "m" | "ft" -- cuma memengaruhi label di canvas
  const [showRuler, setShowRuler] = useState(false);

  // Posisi layar LED di dinding, sebagai FRAKSI 0..1 dari sisa ruang kosong
  // di sekitarnya (0 = mepet kiri/atas, 0.5 = tengah, 1 = mepet kanan/bawah).
  // Dipakai baik untuk drag langsung di canvas maupun tombol preset
  // alignment di panel kiri. Fraksi (bukan meter absolut) dipilih supaya
  // posisi tetap valid & proporsional walau ukuran layar/dinding berubah.
  const [screenPosX, setScreenPosX] = useState(0.5);
  const [screenPosY, setScreenPosY] = useState(0.5);

  // Garis pemisah antar cabinet (bezel) -- default disembunyikan supaya
  // tampilan layar lebih bersih/mulus, bisa dinyalakan lagi lewat toolbar
  // canvas kalau butuh lihat susunan cabinet secara teknis.
  const [showBezel, setShowBezel] = useState(false);

  // Nama project -- ditampilkan di TENGAH layar LED (di atas logo MLV
  // Teknologi) dan bisa diedit lewat navbar, persis pola yang sama
  // dengan RoomDesigner supaya konsisten antar mode.
  const [projectName, setProjectName] = useState("Write project name here");
  const [isEditingName, setIsEditingName] = useState(false);

  const [showExportPdf, setShowExportPdf] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);

  useEffect(() => {
    fetchTheme().then(({ theme }) => setTheme(theme));
    fetchLedProducts().then(({ products }) => {
      setProducts(products);
      setLoadingProducts(false);
    });
  }, []);

  // Produk yang ditampilkan mengikuti environment yang dipilih di Step 1
  // (persis alur MAXHUB: Define Environment dulu, baru produk yang relevan
  // muncul sebagai pilihan).
  const filteredProducts = useMemo(
    () => products.filter((p) => p.environment === environment),
    [products, environment]
  );

  // Kalau environment berubah dan produk yang sedang dipilih tidak lagi
  // cocok (atau belum ada pilihan sama sekali), otomatis pilihkan produk
  // pertama dari daftar yang sudah difilter.
  useEffect(() => {
    if (filteredProducts.length === 0) {
      setProductId("");
      return;
    }
    if (!filteredProducts.some((p) => p.id === productId)) {
      setProductId(filteredProducts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProducts]);

  const product = useMemo(() => products.find((p) => p.id === productId) || null, [products, productId]);
  const layout = useMemo(() => computeLedLayout(product, Number(wallWidthM), Number(wallHeightM)), [product, wallWidthM, wallHeightM]);

  // Jarak layar ke tiap tepi dinding (dalam meter) -- dihitung dari posisi
  // fraksi screenPosX/Y & sisa ruang kosong di sekeliling layar. Rumus ini
  // sengaja disamakan persis dengan yang dipakai LedWallCanvas2D untuk
  // menempatkan layar (freeXPx * posX dkk, cuma di sini dalam satuan meter
  // bukan pixel) supaya nilainya selalu konsisten dengan yang tergambar.
  const wallGaps = useMemo(() => {
    if (!layout) return null;
    const dispRoomWm = Math.max(Number(roomWidthM) || 0, layout.actualWidthM, 1);
    const dispRoomHm = Math.max(Number(roomHeightM) || 0, layout.actualHeightM, 1);
    const freeWm = Math.max(0, dispRoomWm - layout.actualWidthM);
    const freeHm = Math.max(0, dispRoomHm - layout.actualHeightM);
    return {
      left: freeWm * screenPosX,
      right: freeWm * (1 - screenPosX),
      top: freeHm * screenPosY,
      bottom: freeHm * (1 - screenPosY),
    };
  }, [layout, roomWidthM, roomHeightM, screenPosX, screenPosY]);

  // Step ukuran layar dinaikkan/diturunkan per 1 cabinet penuh (selaras
  // dengan computeLedLayout yang membulatkan ke kelipatan cabinet) --
  // kalau belum ada produk terpilih, pakai step 0.1 m sebagai fallback.
  const cabWStepM = product ? product.cabinet_width_mm / 1000 : 0.1;
  const cabHStepM = product ? product.cabinet_height_mm / 1000 : 0.1;

  // Batas maksimum ukuran layar = ukuran ruangan/dinding, dibulatkan ke
  // bawah ke kelipatan cabinet terdekat -- supaya layar TIDAK PERNAH bisa
  // melebihi ukuran dinding, baik lewat tombol +/- di canvas maupun input manual.
  const maxScreenWidthM = Math.max(cabWStepM, Math.floor((Number(roomWidthM) || 0) / cabWStepM) * cabWStepM);
  const maxScreenHeightM = Math.max(cabHStepM, Math.floor((Number(roomHeightM) || 0) / cabHStepM) * cabHStepM);

  const incScreenWidth = useCallback(() => {
    setWallWidthM((w) => Math.min(maxScreenWidthM, Math.round((Number(w) + cabWStepM) * 1000) / 1000));
  }, [cabWStepM, maxScreenWidthM]);
  const decScreenWidth = useCallback(() => {
    setWallWidthM((w) => Math.max(cabWStepM, Math.round((Number(w) - cabWStepM) * 1000) / 1000));
  }, [cabWStepM]);
  const incScreenHeight = useCallback(() => {
    setWallHeightM((h) => Math.min(maxScreenHeightM, Math.round((Number(h) + cabHStepM) * 1000) / 1000));
  }, [cabHStepM, maxScreenHeightM]);
  const decScreenHeight = useCallback(() => {
    setWallHeightM((h) => Math.max(cabHStepM, Math.round((Number(h) - cabHStepM) * 1000) / 1000));
  }, [cabHStepM]);

  // Setiap kali produk berganti, ukuran layar di-set ulang ke 1x1 cabinet
  // sebagai titik awal (sesuai permintaan), baru dari situ user bisa
  // menambah lewat tombol +/-.
  useEffect(() => {
    if (product) {
      setWallWidthM(product.cabinet_width_mm / 1000);
      setWallHeightM(product.cabinet_height_mm / 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  // Kalau ruangan diperkecil (atau produk/pixel pitch berganti) sehingga
  // ukuran layar saat ini jadi lebih besar dari dinding, layar otomatis
  // disusutkan supaya tetap valid.
  useEffect(() => {
    setWallWidthM((w) => (Number(w) > maxScreenWidthM ? maxScreenWidthM : w));
    setWallHeightM((h) => (Number(h) > maxScreenHeightM ? maxScreenHeightM : h));
  }, [maxScreenWidthM, maxScreenHeightM]);

  const handleReset = useCallback(() => {
    if (product) {
      setWallWidthM(product.cabinet_width_mm / 1000);
      setWallHeightM(product.cabinet_height_mm / 1000);
    }
    setRoomWidthM(5);
    setRoomHeightM(3);
    setUnit("m");
    setShowRuler(false);
    setScreenPosX(0.5);
    setScreenPosY(0.5);
  }, [product]);

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", height: "100vh",
        background: "var(--app-bg)", fontFamily: "system-ui,sans-serif",
        "--app-bg": theme.theme_bg,
        "--app-surface": theme.theme_surface,
        "--app-text": theme.theme_text || "#0f172a",
        "--app-text-muted": theme.theme_text_muted || "#94a3b8",
        "--app-border": theme.theme_border || "#e2e8f0",
        "--app-accent": theme.theme_accent,
        "--app-accent-text": theme.theme_accent_text,
      }}
    >
      <nav style={{ height: 60, background: "var(--app-surface)", borderBottom: "1px solid var(--app-border)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", flexShrink: 0, zIndex: 100, boxShadow: "0 2px 8px rgba(15,23,42,0.04)", position: "relative" }}>
        <ModeSwitcher />
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img src={LogoMLV} alt="MLV" style={{ height: 34, objectFit: "contain" }} />
          <div style={{ width: 1, height: 20, background: "var(--app-border)" }} />
          {isEditingName ? (
            <input
              autoFocus value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => e.key === "Enter" && setIsEditingName(false)}
              style={{ background: "var(--app-bg)", border: "1px solid var(--app-border)", borderRadius: 6, padding: "4px 10px", color: "var(--app-text)", fontSize: 14, outline: "none" }}
            />
          ) : (
            <button
              onClick={() => setIsEditingName(true)}
              style={{ background: "none", border: "none", color: "var(--app-text)", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, padding: "4px 8px", borderRadius: 6 }}
            >
              {projectName}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--app-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={() => setShowExportPdf(true)}
          disabled={!product || !layout}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "9px 18px", borderRadius: 8, border: "none", background: "var(--app-accent)",
            color: "var(--app-accent-text)", fontSize: 13, fontWeight: 700, cursor: product ? "pointer" : "default",
            opacity: product ? 1 : 0.5,
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" />
            <path d="M7 10l5 5 5-5" />
            <path d="M4 19h16" />
          </svg>
          Export PDF
        </button>
      </nav>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* PANEL KIRI -- pilih produk & ukuran */}
        <div style={{ width: 300, flexShrink: 0, background: "var(--app-surface)", borderRight: "1px solid var(--app-border)", overflowY: "auto", padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--app-text)", marginBottom: 4 }}>Konfigurasi LED Wall</div>
          <div style={{ fontSize: 11.5, color: "var(--app-text-muted)", marginBottom: 18, lineHeight: 1.5 }}>
            Pilih produk & ukuran dinding -- sistem menghitung layout cabinet, resolusi, berat, dan estimasi daya secara otomatis.
          </div>

          {loadingProducts ? (
            <div style={{ fontSize: 12, color: "var(--app-text-muted)" }}>Memuat katalog...</div>
          ) : products.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--app-text-muted)", background: "var(--app-bg)", padding: 12, borderRadius: 8 }}>
              Belum ada produk LED di katalog. Jalankan <code>supabase/schema_led_configurator.sql</code> di Supabase SQL Editor.
            </div>
          ) : (
            <>
              {/* STEP 1 -- Define Environment */}
              <StepLabel n={1} title="Define Environment" />
              <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                {[{ key: "indoor", label: "Indoor" }, { key: "outdoor", label: "Outdoor" }].map((opt) => {
                  const active = environment === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setEnvironment(opt.key)}
                      style={{
                        flex: 1, padding: "10px 0", borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                        border: active ? "1.5px solid var(--app-accent)" : "1px solid var(--app-border)",
                        background: active ? "var(--app-accent)" : "var(--app-bg)",
                        color: active ? "var(--app-accent-text)" : "var(--app-text-muted)",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* STEP 2 -- Ukuran Ruangan (dinding tempat layar dipasang) */}
              <StepLabel n={2} title="Ukuran Ruangan" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                <div>
                  <label style={labelStyle}>Lebar (m)</label>
                  <input type="number" min="1" step="0.1" value={roomWidthM} onChange={(e) => setRoomWidthM(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Tinggi (m)</label>
                  <input type="number" min="1" step="0.1" value={roomHeightM} onChange={(e) => setRoomHeightM(e.target.value)} style={inputStyle} />
                </div>
              </div>

              {/* STEP 3 -- Pilih Produk (sudah difilter sesuai environment) */}
              <StepLabel n={3} title="Pilih Produk" />
              {filteredProducts.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--app-text-muted)", background: "var(--app-bg)", border: "1px solid var(--app-border)", borderRadius: 8, padding: 10, marginBottom: 16 }}>
                  Belum ada produk untuk environment <strong>{environment}</strong>. Tambahkan lewat halaman admin.
                </div>
              ) : (
                <>
                  {product && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--app-bg)", border: "1px solid var(--app-border)", borderRadius: 10, padding: 10, marginBottom: 8 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 7, background: "#e2e8f0", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                      }}>
                        {product.image_url
                          ? <img src={product.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700 }}>LED</span>}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: "var(--app-text-muted)" }}>Produk yang dipakai</div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--app-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {product.name} -- P{product.pixel_pitch_mm}
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowProductModal(true)}
                    style={{
                      width: "100%", padding: "10px 0", borderRadius: 9, border: "1px solid var(--app-accent)",
                      background: "var(--app-bg)", color: "var(--app-accent)", fontSize: 12.5, fontWeight: 700,
                      cursor: "pointer", marginBottom: 14,
                    }}
                  >
                    Pilih Produk
                  </button>
                </>
              )}

              {product && (
                <>
                  {/* STEP 4 -- Ukuran Layar */}
                  <StepLabel n={4} title="Ukuran Layar" />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
                    <div>
                      <label style={labelStyle}>Lebar (m)</label>
                      <input
                        type="number" min={cabWStepM} max={maxScreenWidthM} step={cabWStepM} value={wallWidthM}
                        onChange={(e) => setWallWidthM(Math.min(maxScreenWidthM, Math.max(0, Number(e.target.value) || 0)))}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Tinggi (m)</label>
                      <input
                        type="number" min={cabHStepM} max={maxScreenHeightM} step={cabHStepM} value={wallHeightM}
                        onChange={(e) => setWallHeightM(Math.min(maxScreenHeightM, Math.max(0, Number(e.target.value) || 0)))}
                        style={inputStyle}
                      />
                    </div>
                    <br></br>
                  </div>

                  {/* STEP 5 -- Posisi Layar di Dinding */}
                  <StepLabel n={5} title="Posisi Layar di Dinding" />
                  <div style={{ fontSize: 11, color: "var(--app-text-muted)", marginBottom: 10, lineHeight: 1.5 }}>
                    Geser langsung layar di canvas (klik & tarik), atau pakai preset di bawah untuk posisi presisi.
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>Horizontal</label>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[{ v: 0, label: "Kiri" }, { v: 0.5, label: "Tengah" }, { v: 1, label: "Kanan" }].map((opt) => {
                        const active = Math.abs(screenPosX - opt.v) < 0.001;
                        return (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => setScreenPosX(opt.v)}
                            style={{
                              flex: 1, padding: "8px 0", borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                              border: active ? "1.5px solid var(--app-accent)" : "1px solid var(--app-border)",
                              background: active ? "var(--app-accent)" : "var(--app-bg)",
                              color: active ? "var(--app-accent-text)" : "var(--app-text-muted)",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Vertikal</label>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[{ v: 0, label: "Atas" }, { v: 0.5, label: "Tengah" }, { v: 1, label: "Bawah" }].map((opt) => {
                        const active = Math.abs(screenPosY - opt.v) < 0.001;
                        return (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => setScreenPosY(opt.v)}
                            style={{
                              flex: 1, padding: "8px 0", borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                              border: active ? "1.5px solid var(--app-accent)" : "1px solid var(--app-border)",
                              background: active ? "var(--app-accent)" : "var(--app-bg)",
                              color: active ? "var(--app-accent-text)" : "var(--app-text-muted)",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {layout && (
                <div style={{ border: "1px solid var(--app-border)", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ background: "var(--app-bg)", padding: "8px 12px", fontSize: 11, fontWeight: 800, color: "var(--app-text-muted)", textTransform: "uppercase" }}>Spesifikasi</div>
                  <StatRow label="LED Display Cabinet" value={`${product.name} (${fmt(layout.totalCabinets)} Pcs)`} />
                  <StatRow label="Ukuran Aktual" value={`${fmt(layout.actualWidthM, 2)} x ${fmt(layout.actualHeightM, 2)} m`} />
                  <StatRow label="Susunan Cabinet" value={`${layout.cols} x ${layout.rows}`} />
                  <StatRow label="Resolusi" value={`${fmt(layout.resolutionW)} x ${fmt(layout.resolutionH)} px`} />
                  <StatRow label="Luas Layar" value={`${fmt(layout.areaM2, 2)} m2`} />
                  <StatRow label="Berat Total" value={`${fmt(layout.totalWeightKg)} kg`} />
                  {wallGaps && <StatRow label="Jarak ke Dinding (Kiri / Kanan)" value={`${formatLen(wallGaps.left, unit, 2)} / ${formatLen(wallGaps.right, unit, 2)}`} />}
                  {wallGaps && <StatRow label="Jarak ke Dinding (Atas / Bawah)" value={`${formatLen(wallGaps.top, unit, 2)} / ${formatLen(wallGaps.bottom, unit, 2)}`} />}
                  {layout.avgPowerW != null && <StatRow label="Daya Rata-rata" value={`${fmt(layout.avgPowerW / 1000, 2)} kW`} />}
                  {layout.maxPowerW != null && <StatRow label="Daya Maksimum" value={`${fmt(layout.maxPowerW / 1000, 2)} kW`} last />}
                </div>
              )}
            </>
          )}
        </div>

        {/* CANVAS -- dinding/ruangan penuh, gaya MAXHUB LED Configurator */}
        <div style={{ flex: 1, position: "relative" }}>
          <LedWallCanvas2D
            product={product}
            layout={layout}
            themePreset={theme.theme_preset}
            roomWidthM={Number(roomWidthM) || 0}
            roomHeightM={Number(roomHeightM) || 0}
            wallWidthM={Number(wallWidthM) || 0}
            wallHeightM={Number(wallHeightM) || 0}
            unit={unit}
            showRuler={showRuler}
            screenPosX={screenPosX}
            screenPosY={screenPosY}
            onScreenPosChange={(x, y) => { setScreenPosX(x); setScreenPosY(y); }}
            showBezel={showBezel}
            onToggleBezel={() => setShowBezel((b) => !b)}
            projectName={projectName}
            onIncWidth={incScreenWidth}
            onDecWidth={decScreenWidth}
            onIncHeight={incScreenHeight}
            onDecHeight={decScreenHeight}
            onReset={handleReset}
            onToggleUnit={() => setUnit((u) => (u === "m" ? "ft" : "m"))}
            onToggleRuler={() => setShowRuler((r) => !r)}
          />
        </div>
      </div>

      {showProductModal && (
        <ProductSelectModal
          products={filteredProducts}
          currentProductId={productId}
          onChoose={(id) => { setProductId(id); setShowProductModal(false); }}
          onClose={() => setShowProductModal(false)}
        />
      )}

      {showExportPdf && (
        <ExportPdfView
          product={product}
          layout={layout}
          roomWidthM={Number(roomWidthM) || 0}
          roomHeightM={Number(roomHeightM) || 0}
          wallWidthM={Number(wallWidthM) || 0}
          wallHeightM={Number(wallHeightM) || 0}
          unit={unit}
          themePreset={theme.theme_preset}
          screenPosX={screenPosX}
          screenPosY={screenPosY}
          showBezel={showBezel}
          wallGaps={wallGaps}
          projectName={projectName}
          onClose={() => setShowExportPdf(false)}
        />
      )}
    </div>
  );
}

function StepLabel({ n, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <div style={{
        width: 20, height: 20, borderRadius: "50%", background: "var(--app-accent)", color: "var(--app-accent-text)",
        fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        {n}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: "var(--app-text)" }}>{title}</div>
    </div>
  );
}

// -------------------------------------------------------------------
// POPUP "PILIH PRODUK" -- gaya MAXHUB LED Configurator: kartu besar di
// atas menampilkan preview produk yang sedang disorot (foto + ringkasan
// spesifikasi + tombol konfirmasi), dan tabel semua produk yang bisa
// diklik untuk mengganti preview di kartu atas.
// -------------------------------------------------------------------
function ProductSelectModal({ products, currentProductId, onChoose, onClose }) {
  const [previewId, setPreviewId] = useState(
    products.some((p) => p.id === currentProductId) ? currentProductId : (products[0]?.id || "")
  );
  const preview = products.find((p) => p.id === previewId) || products[0] || null;

  const bullets = preview
    ? [
        preview.led_type ? `LED Type: ${preview.led_type}` : null,
        preview.brightness_nits ? `Brightness: ${fmt(preview.brightness_nits)}Nit` : null,
        preview.refresh_rate_hz ? `Refresh Rate: ${fmt(preview.refresh_rate_hz)}Hz` : null,
        preview.color_processing_depth ? `Color Processing Depth: ${preview.color_processing_depth}` : null,
        preview.contrast_ratio ? `Contrast: ${preview.contrast_ratio}` : null,
        preview.maintenance ? `Maintenance: ${preview.maintenance}` : null,
      ].filter(Boolean)
    : [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "var(--app-surface)", borderRadius: 16, width: 860, maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "22px 28px 10px", position: "relative", flexShrink: 0 }}>
          <button
            onClick={onClose}
            style={{ position: "absolute", top: 18, right: 22, background: "none", border: "none", fontSize: 20, color: "var(--app-text-muted)", cursor: "pointer", lineHeight: 1 }}
          >
            ×
          </button>
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--app-text)", textAlign: "center" }}>Product Model Selection</div>
          <div style={{ fontSize: 12.5, color: "var(--app-text-muted)", textAlign: "center", marginTop: 4 }}>Please select a model for configuration</div>
        </div>

        <div style={{ overflowY: "auto", padding: "10px 28px 28px" }}>
          {/* Kartu preview besar */}
          {preview && (
            <div style={{ display: "flex", border: "1px solid var(--app-border)", borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
              <div style={{ width: 260, flexShrink: 0, background: "var(--app-bg)", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
                {preview.image_url
                  ? <img src={preview.image_url} alt={preview.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <span style={{ fontSize: 13, color: "var(--app-text-muted)", fontWeight: 700 }}>Tidak ada foto</span>}
              </div>
              <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 19, fontWeight: 800, color: "var(--app-text)", marginBottom: 10 }}>{preview.name}</div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", flex: 1 }}>
                  {bullets.length === 0 ? (
                    <li style={{ fontSize: 12.5, color: "var(--app-text-muted)" }}>Belum ada detail spesifikasi untuk produk ini.</li>
                  ) : (
                    bullets.map((b, i) => (
                      <li key={i} style={{ fontSize: 12.5, color: "var(--app-text)", marginBottom: 6, paddingLeft: 14, position: "relative" }}>
                        <span style={{ position: "absolute", left: 0 }}>•</span>{b}
                      </li>
                    ))
                  )}
                </ul>
                <button
                  onClick={() => onChoose(preview.id)}
                  style={{
                    alignSelf: "flex-start", marginTop: 12, padding: "10px 20px", borderRadius: 8, border: "none",
                    background: "var(--app-accent)", color: "var(--app-accent-text)", fontSize: 12.5, fontWeight: 800,
                    textTransform: "uppercase", letterSpacing: 0.4, cursor: "pointer",
                  }}
                >
                  Choose This Model
                </button>
              </div>
            </div>
          )}

          {/* Tabel semua produk */}
          <div style={{ border: "1px solid var(--app-border)", borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: "var(--app-bg)", textAlign: "left" }}>
                  <th style={thStyle}>Product Series</th>
                  <th style={thStyle}>Model Name</th>
                  <th style={thStyle}>LED Type</th>
                  <th style={thStyle}>Dimensions</th>
                  <th style={thStyle}>Pixel Pitch</th>
                  <th style={thStyle}>Aspect Ratio</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const active = p.id === previewId;
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setPreviewId(p.id)}
                      style={{
                        cursor: "pointer", borderTop: "1px solid var(--app-border)",
                        background: active ? "var(--app-accent)" : "transparent",
                        color: active ? "var(--app-accent-text)" : "var(--app-text)",
                      }}
                    >
                      <td style={tdStyle}>{p.series || "--"}</td>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>{p.name}</td>
                      <td style={tdStyle}>{p.led_type || "--"}</td>
                      <td style={tdStyle}>{p.cabinet_width_mm}×{p.cabinet_height_mm}×{p.cabinet_depth_mm || 80}mm</td>
                      <td style={tdStyle}>{p.pixel_pitch_mm}</td>
                      <td style={tdStyle}>{aspectRatioLabel(p.cabinet_width_mm, p.cabinet_height_mm)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const thStyle = { padding: "10px 14px", fontSize: 10.5, fontWeight: 800, color: "var(--app-text-muted)", textTransform: "uppercase", letterSpacing: 0.3 };
const tdStyle = { padding: "10px 14px" };

function StatRow({ label, value, last }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", fontSize: 12.5, borderBottom: last ? "none" : "1px solid var(--app-border)" }}>
      <span style={{ color: "var(--app-text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 700, color: "var(--app-text)" }}>{value}</span>
    </div>
  );
}

// -------------------------------------------------------------------
// CANVAS 2D -- gaya MAXHUB LED Configurator: dinding/ruangan digambar
// utuh dengan garis dimensi lebar & tinggi, layar LED diposisikan di
// dalamnya lengkap dengan tombol -/+ untuk mengubah ukuran layar
// langsung dari canvas, siluet orang untuk referensi skala, estimasi
// jarak pandang terbaik, serta toolbar Reset / Ruler / Unit.
// Murni SVG + overlay HTML (tidak ada Three.js) -- menyesuaikan ukuran
// lewat viewBox berbanding tetap, jadi overlay HTML selalu presisi.
// -------------------------------------------------------------------
function LedWallCanvas2D({
  product, layout, themePreset, roomWidthM, roomHeightM, wallWidthM, wallHeightM,
  unit, showRuler, screenPosX, screenPosY, onScreenPosChange, showBezel, onToggleBezel,
  projectName,
  onIncWidth, onDecWidth, onIncHeight, onDecHeight, onReset, onToggleUnit, onToggleRuler,
  hideControls,
}) {
  const VB_W = 1000;
  const VB_H = 700;
  const screenClipId = `led-screen-clip-${useId().replace(/[^a-zA-Z0-9-]/g, "")}`;
  const svgRef = useRef(null);
  const dragRef = useRef(null); // { startX, startY, startPosX, startPosY } dalam koordinat viewBox

  const dark = themePreset === "dark";
  const bg = dark ? "#0b1220" : "#f8fafc";
  const roomFill = dark ? "#1e293b" : "#d3d7dc";
  const floorColor = dark ? "#111c2e" : "#c3c9d1";
  const textColor = dark ? "#cbd5e1" : "#64748b";
  const textStrong = dark ? "#e2e8f0" : "#334155";
  const dimColor = dark ? "#60a5fa" : "#2563eb";
  const gridLine = dark ? "#334155" : "#cbd5e1";

  const toolbar = hideControls ? null : (
    <CanvasToolbar dark={dark} unit={unit} showRuler={showRuler} onReset={onReset} onToggleUnit={onToggleUnit} onToggleRuler={onToggleRuler} showBezel={showBezel} onToggleBezel={onToggleBezel} />
  );

  if (!product || !layout) {
    return (
      <div style={{ width: "100%", height: "100%", background: bg, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {toolbar}
        <div style={{ fontSize: 13, color: textColor }}>Pilih produk & isi ukuran layar untuk melihat preview.</div>
      </div>
    );
  }

  // -- Ukuran ruangan/dinding sebenarnya. Layar (screen) sudah dijamin oleh
  // komponen induk tidak akan pernah lebih besar dari ini (di-clamp lewat
  // maxScreenWidthM/maxScreenHeightM), jadi di sini dinding dipakai apa
  // adanya -- layar TIDAK PERNAH melebihi ukuran dinding.
  const dispRoomW = Math.max(Number(roomWidthM) || 0, layout.actualWidthM, 1);
  const dispRoomH = Math.max(Number(roomHeightM) || 0, layout.actualHeightM, 1);

  const PAD_TOP = 76;
  const PAD_LEFT = 130;
  const PAD_RIGHT = 190;
  const PAD_BOTTOM = 190;
  const availW = VB_W - PAD_LEFT - PAD_RIGHT;
  const availH = VB_H - PAD_TOP - PAD_BOTTOM;

  const scale = Math.max(1, Math.min(availW / dispRoomW, availH / dispRoomH));

  const roomPxW = dispRoomW * scale;
  const roomPxH = dispRoomH * scale;
  const roomX = PAD_LEFT + (availW - roomPxW) / 2;
  const roomY = PAD_TOP + (availH - roomPxH) / 2;
  const floorY = roomY + roomPxH;

  // Posisi layar di dinding sekarang BEBAS diatur (drag di canvas atau
  // preset alignment di panel kiri), bukan dipaksa tengah lagi. screenPosX/
  // screenPosY adalah fraksi 0..1 dari sisa ruang kosong di sekeliling
  // layar: 0 = mepet ke tepi kiri/atas, 0.5 = tengah, 1 = mepet ke tepi
  // kanan/bawah. freeXPx/freeYPx adalah sisa ruang geser dalam pixel —
  // kalau layar persis sebesar dinding di salah satu sumbu, nilainya 0
  // (tidak ada ruang geser di sumbu itu, wajar karena layar sudah mepet
  // di kedua sisi sekaligus).
  const screenPxW = layout.actualWidthM * scale;
  const screenPxH = layout.actualHeightM * scale;
  const posX = screenPosX ?? 0.5;
  const posY = screenPosY ?? 0.5;
  const freeXPx = Math.max(0, roomPxW - screenPxW);
  const freeYPx = Math.max(0, roomPxH - screenPxH);
  const screenX = roomX + freeXPx * posX;
  const screenY = roomY + freeYPx * posY;
  const draggable = !hideControls && !!onScreenPosChange && (freeXPx > 0.5 || freeYPx > 0.5);

  // Konversi koordinat pointer (layar browser) ke koordinat viewBox SVG —
  // dibutuhkan karena SVG di-scale via CSS (viewBox 1000x700 tapi ukuran
  // tampil bisa berapa saja mengikuti ukuran panel canvas).
  const clientToViewBox = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return { x: ((clientX - rect.left) / rect.width) * VB_W, y: ((clientY - rect.top) / rect.height) * VB_H };
  };

  const handleScreenPointerDown = (e) => {
    if (!draggable) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const { x, y } = clientToViewBox(e.clientX, e.clientY);
    dragRef.current = { startX: x, startY: y, startPosX: posX, startPosY: posY };
  };
  const handleScreenPointerMove = (e) => {
    if (!dragRef.current) return;
    const { x, y } = clientToViewBox(e.clientX, e.clientY);
    const dPosX = freeXPx > 0 ? (x - dragRef.current.startX) / freeXPx : 0;
    const dPosY = freeYPx > 0 ? (y - dragRef.current.startY) / freeYPx : 0;
    const nextX = Math.min(1, Math.max(0, dragRef.current.startPosX + dPosX));
    const nextY = Math.min(1, Math.max(0, dragRef.current.startPosY + dPosY));
    onScreenPosChange(nextX, nextY);
  };
  const handleScreenPointerUp = (e) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const cabPxW = screenPxW / layout.cols;
  const cabPxH = screenPxH / layout.rows;

  // Garis sambungan antar cabinet (bezel) digambar tipis di atas gambar
  // konten -- dibatasi jumlahnya supaya SVG tidak membengkak kalau
  // cabinet-nya sangat banyak.
  const showBezelGrid = !!showBezel && layout.totalCabinets <= 2500;
  const bezelLines = [];
  if (showBezelGrid) {
    for (let c = 1; c < layout.cols; c++) {
      const x = screenX + c * cabPxW;
      bezelLines.push(<line key={`bv${c}`} x1={x} y1={screenY} x2={x} y2={screenY + screenPxH} stroke="#000" strokeOpacity={0.35} strokeWidth={1} />);
    }
    for (let r = 1; r < layout.rows; r++) {
      const y = screenY + r * cabPxH;
      bezelLines.push(<line key={`bh${r}`} x1={screenX} y1={y} x2={screenX + screenPxW} y2={y} stroke="#000" strokeOpacity={0.35} strokeWidth={1} />);
    }
  }

  // Siluet orang (tinggi rata-rata 1.83m, gaya label MAXHUB "Man 183cm")
  // sebagai referensi skala -- pakai gambar orang yang diunggah, ditaruh
  // di kiri ruangan, berdiri di atas lantai.
  const personHeightM = 1.83;
  const personPxH = personHeightM * scale;
  const personPxW = personPxH * PERSON_IMG_RATIO;
  const personX = roomX - 62;
  const showPerson = personX > 30 && personPxH > 20 && personPxH <= floorY - PAD_TOP;

  // Estimasi jarak pandang terbaik -- aturan umum untuk LED wall:
  // jarak minimum (m) kira-kira pixel pitch (mm) x 1.
  const bestViewDistanceM = product.pixel_pitch_mm ? product.pixel_pitch_mm * 1 : null;

  // Diagonal layar dalam inch
  const diagonalInches = Math.sqrt(layout.actualWidthM ** 2 + layout.actualHeightM ** 2) * M_TO_FT * 12;

  const toPct = (x, y) => ({ left: `${(x / VB_W) * 100}%`, top: `${(y / VB_H) * 100}%` });

  const widthCtrlPos = toPct(screenX + screenPxW / 2, Math.max(screenY - 34, PAD_TOP - 26));
  const heightCtrlPos = toPct(Math.min(screenX + screenPxW + 46 + 44, VB_W - 60), screenY + screenPxH / 2);

  // ── Ruler sungguhan gaya MAXHUB: strip bergaris di tepi atas & kiri
  // ruangan (mirip penggaris fisik) dengan angka meter di setiap garis
  // mayor, ditambah grid bantu tipis di dalam ruangan. Semua muncul saat
  // tombol "Ruler" di toolbar diaktifkan.
  const rulerTicks = [];
  const rulerGrid = [];
  if (showRuler) {
    const stepM = 0.5;
    const EPS = 1e-6;

    const topBaseY = roomY - 10;
    rulerTicks.push(<line key="rt-base" x1={roomX} y1={topBaseY} x2={roomX + roomPxW} y2={topBaseY} stroke={dimColor} strokeWidth={1} opacity={0.55} />);
    for (let x = 0; x <= dispRoomW + EPS; x += stepM) {
      const isMajor = Math.round(x / stepM) % 2 === 0;
      const px = roomX + x * scale;
      const tickLen = isMajor ? 9 : 4.5;
      rulerTicks.push(<line key={`rt${x}`} x1={px} y1={topBaseY} x2={px} y2={topBaseY - tickLen} stroke={dimColor} strokeWidth={1} opacity={0.65} />);
      if (isMajor && x > 0.01 && x < dispRoomW - 0.01) {
        rulerTicks.push(
          <text key={`rtl${x}`} x={px} y={topBaseY - 12} fontSize={8} fill={dimColor} textAnchor="middle" opacity={0.8}>
            {Math.round(x)}
          </text>
        );
      }
      if (x > 0 && x < dispRoomW) {
        rulerGrid.push(<line key={`rgv${x}`} x1={px} y1={roomY} x2={px} y2={floorY} stroke={gridLine} strokeWidth={0.6} strokeDasharray="2,3" opacity={0.6} />);
      }
    }

    const leftBaseX = roomX - 10;
    rulerTicks.push(<line key="rl-base" x1={leftBaseX} y1={roomY} x2={leftBaseX} y2={floorY} stroke={dimColor} strokeWidth={1} opacity={0.55} />);
    for (let y = 0; y <= dispRoomH + EPS; y += stepM) {
      const isMajor = Math.round(y / stepM) % 2 === 0;
      const py = floorY - y * scale;
      const tickLen = isMajor ? 9 : 4.5;
      rulerTicks.push(<line key={`rl${y}`} x1={leftBaseX} y1={py} x2={leftBaseX - tickLen} y2={py} stroke={dimColor} strokeWidth={1} opacity={0.65} />);
      if (isMajor && y > 0.01 && y < dispRoomH - 0.01) {
        rulerTicks.push(
          <text key={`rll${y}`} x={leftBaseX - 13} y={py + 3} fontSize={8} fill={dimColor} textAnchor="end" opacity={0.8}>
            {Math.round(y)}
          </text>
        );
      }
      if (y > 0 && y < dispRoomH) {
        rulerGrid.push(<line key={`rgh${y}`} x1={roomX} y1={py} x2={roomX + roomPxW} y2={py} stroke={gridLine} strokeWidth={0.6} strokeDasharray="2,3" opacity={0.6} />);
      }
    }
  }

  return (
    <div style={{ width: "100%", height: "100%", background: bg, position: "relative" }}>
      {toolbar}

      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
        <div style={{ position: "relative", width: "100%", height: "100%", maxWidth: `${VB_W}px`, aspectRatio: `${VB_W} / ${VB_H}` }}>
          <svg ref={svgRef} viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%", display: "block" }}>
            {/* Lantai */}
            <rect x={0} y={floorY} width={VB_W} height={VB_H - floorY} fill={floorColor} />

            {/* Dinding / ruangan */}
            <rect x={roomX} y={roomY} width={roomPxW} height={roomPxH} fill={roomFill} />
            {rulerGrid}
            <rect x={roomX} y={roomY} width={roomPxW} height={roomPxH} fill="none" stroke={dark ? "#334155" : "#94a3b8"} strokeWidth={1} />
            {rulerTicks}

            {/* Siluet orang -- referensi skala (gambar yang diunggah user) */}
            {showPerson && (
              <image
                href={PersonSilhouette}
                x={personX - personPxW / 2}
                y={floorY - personPxH}
                width={personPxW}
                height={personPxH}
                preserveAspectRatio="xMidYMax meet"
                opacity={0.85}
              />
            )}

            {/* Konten layar LED -- gambar sungguhan (gaya demo content MAXHUB),
                di-crop penuh (cover) ke ukuran layar lalu diberi garis bezel
                antar cabinet supaya tetap terlihat sebagai susunan cabinet. */}
            <clipPath id={screenClipId}>
              <rect x={screenX} y={screenY} width={screenPxW} height={screenPxH} />
            </clipPath>
            <image
              href={ScreenContentDemo}
              x={screenX} y={screenY} width={screenPxW} height={screenPxH}
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#${screenClipId})`}
            />

            {/* Overlay teks "MLV Teknologi" + nama project, dipusatkan di
                tengah layar LED. Nama project bisa diedit lewat navbar
                (klik ikon pensil) -- perubahannya langsung terlihat di
                sini secara real-time, mirip nama project di RoomDesigner.
                Di-clip ke area layar juga supaya tidak "bocor" keluar
                kalau layarnya kecil/sempit. */}
            <g clipPath={`url(#${screenClipId})`}>
              {(() => {
                // Ukuran logo mengikuti tinggi layar (rasio asli logo 300x92).
                const logoH = Math.max(16, Math.min(40, screenPxH * 0.1));
                const logoW = logoH * (300 / 92);
                const logoWhiteId = `${screenClipId}-logo-white`;
                const cx = screenX + screenPxW / 2;
                const cy = screenY + screenPxH / 2;
                const nameFont = Math.max(8, Math.min(16, screenPxH * 0.05));
                const logoY = cy + nameFont * 0.8;
                return (
                  <>
                    {/* Filter: ubah semua warna logo jadi putih, alpha tetap. */}
                    <defs>
                      <filter id={logoWhiteId} colorInterpolationFilters="sRGB">
                        <feColorMatrix type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 1 0" />
                      </filter>
                    </defs>
                    {projectName && (
                      <text
                        x={cx} y={cy - nameFont * 0.4}
                        textAnchor="middle" dominantBaseline="middle"
                        fontSize={nameFont} fontWeight={600}
                        fill="#fff" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.55)" }}
                      >
                        {projectName}
                      </text>
                    )}
                    <image
                      href={LogoMLV}
                      x={cx - logoW / 2} y={logoY}
                      width={logoW} height={logoH}
                      preserveAspectRatio="xMidYMid meet"
                      filter={`url(#${logoWhiteId})`}
                    />
                  </>
                );
              })()}
            </g>
            {bezelLines}
            <rect x={screenX} y={screenY} width={screenPxW} height={screenPxH} fill="none" stroke={dimColor} strokeWidth={2} />

            {/* Overlay transparan untuk drag posisi layar -- cuma aktif kalau
                masih ada ruang geser (freeXPx/freeYPx > 0) & bukan mode
                readonly (hideControls, dipakai di preview PDF). Cursor
                berubah jadi "move" sebagai affordance visual. */}
            {draggable && (
              <rect
                x={screenX} y={screenY} width={screenPxW} height={screenPxH}
                fill="#000" opacity={0}
                style={{ cursor: dragRef.current ? "grabbing" : "grab", touchAction: "none" }}
                onPointerDown={handleScreenPointerDown}
                onPointerMove={handleScreenPointerMove}
                onPointerUp={handleScreenPointerUp}
                onPointerCancel={handleScreenPointerUp}
              />
            )}

            {/* Dimensi lebar layar (garis putus-putus di atas layar) */}
            <DimLine x1={screenX} x2={screenX + screenPxW} y={screenY - 46} color={dimColor} dashed />

            {/* Dimensi tinggi layar (garis putus-putus di kanan layar) */}
            <DimLine x1={screenY} x2={screenY + screenPxH} y={screenX + screenPxW + 46} color={dimColor} vertical dashed />

            {/* Jarak layar ke tepi dinding (kiri/kanan/atas/bawah) -- warna
                abu-abu (textColor), BEDA dari dimensi ukuran layar yang biru
                (dimColor), supaya gampang dibedakan sekilas: biru = ukuran
                LAYAR, abu-abu = sisa jarak ke DINDING. Cuma muncul kalau
                ada jarak yang berarti (>6px) supaya tidak nongol "0m" pas
                layar sudah mepet ke tepi itu. */}
            {(() => {
              const gapLeftPx = screenX - roomX;
              const gapRightPx = roomX + roomPxW - (screenX + screenPxW);
              const gapTopPx = screenY - roomY;
              const gapBottomPx = roomY + roomPxH - (screenY + screenPxH);
              // Offset dari titik tengah layar -- dipakai SAMA PERSIS untuk
              // pasangan kiri/kanan (align di Y yang sama) dan pasangan
              // atas/bawah (align di X yang sama), supaya sejajar rapi.
              // Nilainya sengaja tidak tepat 0.5 (dead-center) untuk
              // menghindari tabrakan dengan kontrol +/- ukuran layar yang
              // memang diposisikan di tengah atas & tengah kanan.
              const gapLineY = screenY + screenPxH * 0.28;
              const gapLineX = screenX + screenPxW * 0.28;
              return (
                <>
                  {gapLeftPx > 6 && (
                    <>
                      <DimLine x1={roomX} x2={screenX} y={gapLineY} color={textColor} dashed />
                      <text x={roomX + gapLeftPx / 2} y={gapLineY - 8} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={textColor}>
                        {formatLen(gapLeftPx / scale, unit, 2)}
                      </text>
                    </>
                  )}
                  {gapRightPx > 6 && (
                    <>
                      <DimLine x1={screenX + screenPxW} x2={roomX + roomPxW} y={gapLineY} color={textColor} dashed />
                      <text x={screenX + screenPxW + gapRightPx / 2} y={gapLineY - 8} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={textColor}>
                        {formatLen(gapRightPx / scale, unit, 2)}
                      </text>
                    </>
                  )}
                  {gapTopPx > 6 && (
                    <>
                      <DimLine x1={roomY} x2={screenY} y={gapLineX} vertical color={textColor} dashed />
                      <text x={gapLineX + 10} y={roomY + gapTopPx / 2} textAnchor="start" dominantBaseline="middle" fontSize={10.5} fontWeight={700} fill={textColor}>
                        {formatLen(gapTopPx / scale, unit, 2)}
                      </text>
                    </>
                  )}
                  {gapBottomPx > 6 && (
                    <>
                      <DimLine x1={screenY + screenPxH} x2={roomY + roomPxH} y={gapLineX} vertical color={textColor} dashed />
                      <text x={gapLineX + 10} y={screenY + screenPxH + gapBottomPx / 2} textAnchor="start" dominantBaseline="middle" fontSize={10.5} fontWeight={700} fill={textColor}>
                        {formatLen(gapBottomPx / scale, unit, 2)}
                      </text>
                    </>
                  )}
                </>
              );
            })()}

            {/* Dimensi tinggi ruangan (kiri) */}
            <DimLine x1={roomY} x2={floorY} y={roomX - 44} color={dimColor} vertical />
            <text
              x={roomX - 58} y={roomY + roomPxH / 2}
              textAnchor="middle" fontSize={13} fontWeight={700} fill={dimColor}
              transform={`rotate(-90, ${roomX - 58}, ${roomY + roomPxH / 2})`}
            >
              {formatLen(dispRoomH, unit, 2)}
            </text>

            {/* Dimensi lebar ruangan (bawah) */}
            <DimLine x1={roomX} x2={roomX + roomPxW} y={floorY + 34} color={dimColor} />
            <text x={roomX + roomPxW / 2} y={floorY + 58} textAnchor="middle" fontSize={13} fontWeight={700} fill={dimColor}>
              {formatLen(dispRoomW, unit, 2)}
            </text>

            {/* Estimasi jarak pandang terbaik */}
            {showPerson && bestViewDistanceM != null && (
              <>
                <text x={personX} y={floorY + 20} fontSize={10.5} fill={textColor} textAnchor="middle">
                  {formatLen(bestViewDistanceM, unit, 1)}
                </text>
                <text x={personX} y={floorY + 34} fontSize={9.5} fill={textColor} textAnchor="middle" opacity={0.75}>
                  Jarak Pandang Terbaik
                </text>
                <text x={personX} y={floorY + 47} fontSize={9} fill={textColor} textAnchor="middle" opacity={0.55}>
                  *Orang 183cm
                </text>
              </>
            )}

            {/* Diagonal, Resolusi & aspect ratio -- dikelompokkan jadi satu blok
                di bawah dinding supaya tidak tumpang tindih dengan navbar
                mode switcher di bagian atas canvas. */}
            <text x={roomX + roomPxW / 2} y={floorY + 78} textAnchor="middle" fontSize={13} fontWeight={700} fill={textStrong}>
              Diagonal: {fmt(diagonalInches, 0)} Inch
            </text>
            <text x={roomX + roomPxW / 2} y={floorY + 98} textAnchor="middle" fontSize={12} fill={textStrong}>
              Resolusi: {fmt(layout.resolutionW)} x {fmt(layout.resolutionH)}
            </text>
            <text x={roomX + roomPxW / 2} y={floorY + 118} textAnchor="middle" fontSize={12} fill={textStrong}>
              Aspect Ratio: {aspectRatioLabel(layout.resolutionW, layout.resolutionH)}
            </text>
          </svg>

          {/* Overlay HTML -- kontrol ukuran lebar layar (atas) */}
          <SizeControl
            style={{ left: widthCtrlPos.left, top: widthCtrlPos.top, transform: "translate(-50%, -50%)" }}
            value={formatLen(layout.actualWidthM, unit, 2)}
            onInc={onIncWidth}
            onDec={onDecWidth}
            dark={dark}
            readOnly={hideControls}
          />

          {/* Overlay HTML -- kontrol ukuran tinggi layar (kanan) */}
          <SizeControl
            style={{ left: heightCtrlPos.left, top: heightCtrlPos.top, transform: "translate(-50%, -50%)" }}
            value={formatLen(layout.actualHeightM, unit, 2)}
            onInc={onIncHeight}
            onDec={onDecHeight}
            dark={dark}
            readOnly={hideControls}
          />
        </div>
      </div>
    </div>
  );
}

function aspectRatioLabel(w, h) {
  if (!w || !h) return "--";
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(Math.round(w), Math.round(h)) || 1;
  return `${Math.round(w / g)}:${Math.round(h / g)}`;
}

function SizeControl({ style, value, onInc, onDec, dark, readOnly }) {
  const boxBg = dark ? "#0f172a" : "#ffffff";
  const boxBorder = dark ? "#334155" : "#cbd5e1";
  const boxText = dark ? "#e2e8f0" : "#334155";
  const btnBg = dark ? "#1e293b" : "#f1f5f9";

  if (readOnly) {
    return (
      <div
        style={{
          position: "absolute", ...style,
          background: boxBg, border: `1px solid ${boxBorder}`, borderRadius: 8,
          boxShadow: "0 2px 6px rgba(15,23,42,0.12)", userSelect: "none", zIndex: 5,
          padding: "5px 12px", fontSize: 12, fontWeight: 700, color: boxText, whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute", ...style, display: "flex", alignItems: "center", gap: 0,
        background: boxBg, border: `1px solid ${boxBorder}`, borderRadius: 8, overflow: "hidden",
        boxShadow: "0 2px 6px rgba(15,23,42,0.12)", userSelect: "none", zIndex: 5,
      }}
    >
      <button
        onClick={onDec}
        aria-label="Kurangi ukuran"
        style={{ width: 26, height: 30, border: "none", background: btnBg, color: boxText, fontSize: 15, fontWeight: 700, cursor: "pointer", lineHeight: 1 }}
      >
        -
      </button>
      <div style={{ padding: "0 10px", fontSize: 12, fontWeight: 700, color: boxText, whiteSpace: "nowrap" }}>{value}</div>
      <button
        onClick={onInc}
        aria-label="Tambah ukuran"
        style={{ width: 26, height: 30, border: "none", background: btnBg, color: boxText, fontSize: 15, fontWeight: 700, cursor: "pointer", lineHeight: 1 }}
      >
        +
      </button>
    </div>
  );
}

function CanvasToolbar({ dark, unit, showRuler, onReset, onToggleUnit, onToggleRuler, showBezel, onToggleBezel }) {
  const iconColor = dark ? "#93c5fd" : "#2563eb";
  const boxBg = dark ? "#0f172a" : "#ffffff";
  const boxBorder = dark ? "#334155" : "#dbeafe";
  const labelColor = dark ? "#94a3b8" : "#64748b";

  const Btn = ({ onClick, active, label, children }) => (
    <button
      onClick={onClick}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
        background: "none", border: "none", cursor: "pointer", padding: 0, width: 56,
      }}
    >
      <div
        style={{
          width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          border: `1.5px solid ${active ? iconColor : boxBorder}`, background: active ? (dark ? "#1e3a5f" : "#eff6ff") : boxBg,
          boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
        }}
      >
        {children}
      </div>
      <span style={{ fontSize: 10.5, color: labelColor, fontWeight: 600 }}>{label}</span>
    </button>
  );

  return (
    <div style={{ position: "absolute", top: 16, left: 16, display: "flex", gap: 14, zIndex: 10 }}>
      <Btn onClick={onReset} label="Reset">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 3-6.7" />
          <path d="M3 4v5h5" />
        </svg>
      </Btn>
      <Btn onClick={onToggleRuler} active={showRuler} label="Ruler">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="8" width="18" height="8" rx="1.5" transform="rotate(-10 12 12)" />
          <path d="M7 9.5v2M10 9v2.5M13 8.7v2M16 8.3v2" />
        </svg>
      </Btn>
      <Btn onClick={onToggleBezel} active={showBezel} label="Bezel">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="1.5" />
          <path d="M9 3v18M15 3v18M3 9h18M3 15h18" />
        </svg>
      </Btn>
      <Btn onClick={onToggleUnit} label={unit === "m" ? "M/FT" : "FT/M"}>
        <span style={{ fontSize: 10, fontWeight: 800, color: iconColor }}>{unit === "m" ? "M" : "FT"}</span>
      </Btn>
    </div>
  );
}

function DimLine({ x1, x2, y, color, vertical, dashed }) {
  const dashProps = dashed ? { strokeDasharray: "4,4" } : {};
  if (vertical) {
    return (
      <g stroke={color} strokeWidth={1.3} {...dashProps}>
        <line x1={y} y1={x1} x2={y} y2={x2} />
        <line x1={y - 5} y1={x1} x2={y + 5} y2={x1} />
        <line x1={y - 5} y1={x2} x2={y + 5} y2={x2} />
      </g>
    );
  }
  return (
    <g stroke={color} strokeWidth={1.3} {...dashProps}>
      <line x1={x1} y1={y} x2={x2} y2={y} />
      <line x1={x1} y1={y - 5} x2={x1} y2={y + 5} />
      <line x1={x2} y1={y - 5} x2={x2} y2={y + 5} />
    </g>
  );
}


// -------------------------------------------------------------------
// EXPORT PDF -- berisi snapshot canvas (lengkap dengan ruler & dimensi)
// plus tabel spesifikasi produk (termasuk field "Spesifikasi Tambahan"
// yang diisi manual oleh tim marketing lewat halaman admin). Memakai
// dialog print bawaan browser (Save as PDF) supaya tidak perlu
// dependency tambahan dan hasilnya tetap vektor/tajam.
// -------------------------------------------------------------------
function ExportPdfView({ product, layout, roomWidthM, roomHeightM, wallWidthM, wallHeightM, unit, themePreset, screenPosX, screenPosY, showBezel, wallGaps, projectName, onClose }) {
  const handlePrint = () => window.print();
  const today = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const extraSpecs = Array.isArray(product?.extra_specs) ? product.extra_specs.filter((s) => s.label || s.value) : [];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #led-print-area, #led-print-area * { visibility: visible; }
          #led-print-area { position: fixed; inset: 0; padding: 32px; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div style={{ background: "#fff", borderRadius: 16, width: 640, maxWidth: "95vw", maxHeight: "92vh", overflow: "auto", color: "#0f172a" }}>
        <div id="led-print-area" style={{ padding: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <img src={LogoMLV} alt="MLV" style={{ height: 32 }} />
            <div style={{ fontSize: 11, color: "#64748b" }}>{today}</div>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 2 }}>Konfigurasi LED Wall</div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 2 }}>{product?.name} -- MLV Teknologi RoomDesigner</div>
          {projectName && <div style={{ fontSize: 12, color: "#64748b", marginBottom: 18 }}>Project: <strong style={{ color: "#0f172a" }}>{projectName}</strong></div>}

          {/* Snapshot canvas -- termasuk ruler, dimensi ruangan/layar, siluet
              orang, & konten demo, sama persis seperti yang terlihat di
              LED Configurator (dipaksa tema terang & ruler aktif supaya
              jelas saat dicetak). */}
          <div style={{ width: "100%", aspectRatio: "1000 / 700", position: "relative", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
            <LedWallCanvas2D
              product={product}
              layout={layout}
              themePreset="light"
              roomWidthM={roomWidthM}
              roomHeightM={roomHeightM}
              wallWidthM={wallWidthM}
              wallHeightM={wallHeightM}
              unit={unit}
              showRuler={true}
              screenPosX={screenPosX}
              screenPosY={screenPosY}
              showBezel={showBezel}
              projectName={projectName}
              hideControls={true}
            />
          </div>

          <div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 8 }}>Spesifikasi</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: extraSpecs.length ? 18 : 0 }}>
            <tbody>
              <PrintRow label="Produk" value={product?.name} />
              {product?.series ? <PrintRow label="Model / Series" value={product.series} /> : null}
              <PrintRow label="Environment" value={product?.environment} />
              <PrintRow label="Pixel Pitch" value={product ? `P${product.pixel_pitch_mm}` : ""} />
              {product?.brightness_nits ? <PrintRow label="Brightness" value={`${fmt(product.brightness_nits)} nits`} /> : null}
              {layout && <PrintRow label="Ukuran Aktual" value={`${fmt(layout.actualWidthM, 2)} x ${fmt(layout.actualHeightM, 2)} m`} />}
              {layout && <PrintRow label="Susunan Cabinet" value={`${layout.cols} x ${layout.rows} (${fmt(layout.totalCabinets)} pcs)`} />}
              {layout && <PrintRow label="Resolusi" value={`${fmt(layout.resolutionW)} x ${fmt(layout.resolutionH)} px`} />}
              {layout && <PrintRow label="Aspect Ratio" value={aspectRatioLabel(layout.resolutionW, layout.resolutionH)} />}
              {layout && <PrintRow label="Luas Layar" value={`${fmt(layout.areaM2, 2)} m2`} />}
              {layout && <PrintRow label="Berat Total" value={`${fmt(layout.totalWeightKg)} kg`} />}
              {wallGaps && <PrintRow label="Jarak ke Dinding (Kiri / Kanan)" value={`${formatLen(wallGaps.left, unit, 2)} / ${formatLen(wallGaps.right, unit, 2)}`} />}
              {wallGaps && <PrintRow label="Jarak ke Dinding (Atas / Bawah)" value={`${formatLen(wallGaps.top, unit, 2)} / ${formatLen(wallGaps.bottom, unit, 2)}`} />}
              {layout?.avgPowerW != null && <PrintRow label="Daya Rata-rata" value={`${fmt(layout.avgPowerW / 1000, 2)} kW`} />}
              {layout?.maxPowerW != null && <PrintRow label="Daya Maksimum" value={`${fmt(layout.maxPowerW / 1000, 2)} kW`} />}
            </tbody>
          </table>

          {extraSpecs.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", marginBottom: 8 }}>Spesifikasi Tambahan</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <tbody>
                  {extraSpecs.map((s, i) => (
                    <PrintRow key={i} label={s.label || "—"} value={s.value || "—"} />
                  ))}
                </tbody>
              </table>
            </>
          )}

          {product?.notes && (
            <div style={{ fontSize: 12, color: "#475569", marginTop: 16, lineHeight: 1.6, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 12 }}>
              {product.notes}
            </div>
          )}

          <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 18, lineHeight: 1.5 }}>
            *Konfigurasi ini bersifat simulasi. Spesifikasi &amp; harga akhir mengikuti konfirmasi dari tim MLV Teknologi.
          </div>
        </div>

        <div className="no-print" style={{ padding: "0 28px 24px", display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 11, borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontWeight: 600 }}>Tutup</button>
          <button onClick={handlePrint} style={{ flex: 1, padding: 11, borderRadius: 8, border: "none", background: "#3b82f6", color: "#fff", cursor: "pointer", fontWeight: 700 }}>Export ke PDF</button>
        </div>
      </div>
    </div>
  );
}

function PrintRow({ label, value }) {
  return (
    <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
      <td style={{ padding: "8px 0", color: "#64748b", textTransform: "capitalize" }}>{label}</td>
      <td style={{ padding: "8px 0", textAlign: "right", fontWeight: 700 }}>{value ?? "—"}</td>
    </tr>
  );
}